import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField, Button, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { Comment, DefaultComment } from '../../../../services/ticketEasyService/model/ticketEasy/Comment';
import { CommentPostCommand } from '../../../../services/ticketEasyService/commands/CommentPostCommand';
import { useCommentPostMutation } from '../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../services/profileService/profile-api';
import { ticketEasyColors } from '../../TicketEasyTheme';

interface CommentsDialogProps {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  comments: Comment[];
}

export function CommentsDialog({ 
  open, 
  onClose, 
  ticketId,
  comments 
}: CommentsDialogProps) {
  const [newComment, setNewComment] = useState('');
  const [commentPost] = useCommentPostMutation();
  const { data: myProfile } = useMyProfileQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const comment = DefaultComment();
      comment.ticketId = ticketId;
      comment.user = myProfile?.identity || 'Anonymous';
      comment.comment = newComment;
      
      const command = new CommentPostCommand(comment);
      await commentPost(command).unwrap();
      
      setNewComment('');
      onClose();
    } catch (error) {
      console.error('Failed to post comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Add Comment
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Your comment
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Type your comment here..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            variant="outlined"
            sx={{ mb: 1.5 }}
            autoFocus
            size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || isSubmitting}
              startIcon={<SendIcon />}
              sx={{
                bgcolor: ticketEasyColors.psnGreen,
                '&:hover': {
                  bgcolor: '#4ba9aa'
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Comment'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}